/* eslint-disable @typescript-eslint/naming-convention */
import { Listbox, ListboxButton } from '@headlessui/react';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import React, { useCallback, useMemo, useState } from 'react';
import { useViewportSize } from '../../hooks/useViewportSize';
import { Drawer } from '../Drawer/Drawer';
import { drawerContentContainer } from './DrawerSelect.css';
import type { ItemProps, Key } from './Item';
import type { SelectProps } from './Select';
import { Trigger } from './Trigger';

const DRAWER_CLOSE_TIMEOUT: Milliseconds = 30;

export type SelectItem = SelectProps['items'][number] & { readonly key?: Key; readonly label?: string };

const getItemLabel = (items: SelectItem[], value: SelectProps['value']): string | undefined =>
  items.find(child => child.key === value)?.label;

type UseSelectProps = Pick<SelectProps, 'disabled' | 'items' | 'onChange' | 'value'> & {
  readonly wrapperProps: Record<string, unknown>;
};

type UseSelectResult = {
  readonly isMobileViewport: boolean;
  readonly isSelectedItem: (item: ItemProps) => boolean;
  readonly itemsWithKey: SelectItem[];
  readonly label: string | undefined;
  readonly handleAction: (itemValue: Key) => void;
  readonly Wrapper: typeof Drawer.Root | typeof Listbox;
  readonly wrapperProps: React.ComponentProps<typeof Drawer.Root | typeof Listbox>;
  readonly TriggerWrapper: typeof Drawer.Trigger | typeof React.Fragment;
  readonly triggerWrapperProps: React.ComponentProps<typeof Drawer.Trigger> | {};
  readonly ContentWrapper: typeof Drawer.Content | typeof React.Fragment;
  readonly contentWrapperProps: React.ComponentProps<typeof Drawer.Content> & {
    readonly description: string;
    readonly title: string;
  };
  readonly Component: typeof ListboxButton | typeof Trigger;
  readonly triggerWidth: number;
  readonly setTriggerWidth: React.Dispatch<React.SetStateAction<number>>;
};

export const useSelect = ({ items, value, disabled, onChange, wrapperProps }: UseSelectProps): UseSelectResult => {
  const { isMobileViewport } = useViewportSize();
  const [triggerWidth, setTriggerWidth] = useState(0);
  const isSelectedItem = useCallback((item: ItemProps) => item.value === value, [value]);
  const itemsWithKey = useMemo(
    () =>
      items.map(item => ({
        ...item,
        key: item.value,
      })),
    [items],
  );

  const label = useMemo(() => getItemLabel(itemsWithKey, value), [itemsWithKey, value]);

  const [isOpen, setIsOpen] = useState(false);
  const handleAction = useCallback(
    (itemValue: Key) => {
      onChange?.(itemValue);
      setTimeout(() => {
        setIsOpen(false);
      }, DRAWER_CLOSE_TIMEOUT);
    },
    [onChange],
  );

  const contentWrapperProps = {
    className: drawerContentContainer,
    description: label ?? 'Select',
    title: label ?? 'Select',
    triggerWidth,
  };

  const viewComponents = isMobileViewport
    ? {
        Wrapper: Drawer.Root,
        wrapperProps: { open: isOpen, onOpenChange: setIsOpen },
        TriggerWrapper: Drawer.Trigger,
        triggerWrapperProps: { disabled },
        ContentWrapper: Drawer.Content,
        contentWrapperProps,
        Component: Trigger,
      }
    : {
        Wrapper: Listbox,
        wrapperProps: { as: 'div', disabled, onChange, value, ...wrapperProps },
        TriggerWrapper: React.Fragment,
        triggerWrapperProps: {},
        ContentWrapper: React.Fragment,
        contentWrapperProps,
        Component: ListboxButton,
      };

  return {
    isMobileViewport,
    isSelectedItem,
    itemsWithKey,
    label,
    handleAction,
    triggerWidth,
    setTriggerWidth,
    ...viewComponents,
  };
};
